/**
 * AUTOMATION TIPS COMPONENT
 * =========================
 * Provides contextual guidance to help users achieve 100% transaction automation.
 * 
 * Key Messages:
 * 1. Include invoice/bill numbers in payment descriptions
 * 2. Use business accounts only for business transactions
 * 3. Include customer/vendor names in payment references
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Lightbulb, 
  X, 
  ChevronDown, 
  ChevronUp,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Info,
  Zap
} from 'lucide-react';

// Storage key for dismissal state
const TIPS_DISMISSED_KEY = 'akountfly_automation_tips_dismissed';
const TIPS_COLLAPSED_KEY = 'akountfly_automation_tips_collapsed';

/**
 * Collapsible tip banner for Bank Transactions page
 */
export const BankFeedTipBanner = ({ className = '' }) => {
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem(`${TIPS_DISMISSED_KEY}_bankfeed`) === 'true';
  });
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(`${TIPS_COLLAPSED_KEY}_bankfeed`) === 'true';
  });

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(`${TIPS_DISMISSED_KEY}_bankfeed`, 'true');
  };

  const toggleCollapse = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem(`${TIPS_COLLAPSED_KEY}_bankfeed`, newState.toString());
  };

  if (dismissed) return null;

  return (
    <div className={`bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl ${className}`}>
      <div 
        className="flex items-center justify-between px-4 py-3 cursor-pointer"
        onClick={toggleCollapse}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
            <Lightbulb className="w-4 h-4 text-amber-600" />
          </div>
          <div>
            <span className="font-medium text-amber-900">Pro Tip: Maximize Auto-Matching</span>
            {collapsed && (
              <span className="text-amber-700 text-sm ml-2">Click to expand</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {collapsed ? (
            <ChevronDown className="w-5 h-5 text-amber-600" />
          ) : (
            <ChevronUp className="w-5 h-5 text-amber-600" />
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-amber-600 hover:text-amber-800 hover:bg-amber-100"
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss();
            }}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {!collapsed && (
        <div className="px-4 pb-4 pt-1">
          <p className="text-amber-800 text-sm mb-3">
            Transactions with clear descriptions are auto-matched instantly. When making payments:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Include invoice numbers (e.g., "INV-00123 payment")</span>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span className="text-slate-700">Add customer/vendor names in the description</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Info banner for Bank Account linking
 */
export const BankAccountSeparationBanner = ({ className = '' }) => {
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem(`${TIPS_DISMISSED_KEY}_bank_separation`) === 'true';
  });

  if (dismissed) return null;

  return (
    <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-blue-900 font-medium text-sm">Keep Business and Personal Separate</p>
          <p className="text-blue-700 text-sm mt-1">
            Only link accounts used exclusively for business transactions. 
            Mixing personal transactions reduces AI automation accuracy.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-blue-600 hover:text-blue-800 hover:bg-blue-100"
          onClick={() => {
            setDismissed(true);
            localStorage.setItem(`${TIPS_DISMISSED_KEY}_bank_separation`, 'true');
          }}
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Contextual reminder for dashboard when unmatched transactions are high
 */
export const UnmatchedTransactionsTip = ({ unmatchedCount = 0, className = '' }) => {
  const [dismissed, setDismissed] = useState(false);

  // Only show if there are more than 5 unmatched transactions
  if (unmatchedCount < 5 || dismissed) return null;

  return (
    <div className={`bg-slate-50 border border-slate-200 rounded-lg p-3 ${className}`}>
      <div className="flex items-center gap-3">
        <AlertCircle className="w-4 h-4 text-slate-500 flex-shrink-0" />
        <p className="text-slate-600 text-sm flex-1">
          <span className="font-medium">{unmatchedCount} unmatched transactions.</span>
          {' '}Tip: Include invoice numbers in future payments for instant matching.
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
          onClick={() => setDismissed(true)}
        >
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Tooltip for invoice reference number
 */
export const InvoiceReferenceTip = ({ invoiceNumber, children }) => {
  return (
    <div className="group relative inline-flex items-center">
      {children}
      <div className="invisible group-hover:visible absolute left-0 top-full mt-2 z-50 w-64 p-3 bg-slate-900 text-white text-xs rounded-lg shadow-lg">
        <div className="flex items-start gap-2">
          <Sparkles className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium mb-1">Share this reference</p>
            <p className="text-slate-300">
              Ask your customer to include "{invoiceNumber}" in their payment description for automatic matching.
            </p>
          </div>
        </div>
        <div className="absolute -top-1.5 left-4 w-3 h-3 bg-slate-900 rotate-45" />
      </div>
    </div>
  );
};

/**
 * Helper text for payment recording modals
 */
export const PaymentReferenceHelper = ({ referenceNumber, className = '' }) => {
  return (
    <p className={`text-xs text-slate-500 flex items-center gap-1.5 ${className}`}>
      <Zap className="w-3 h-3 text-amber-500" />
      Use "{referenceNumber}" when making bank transfers for automatic matching
    </p>
  );
};

/**
 * Onboarding Best Practices checklist (for use in onboarding flow)
 */
export const OnboardingBestPractices = ({ onContinue, className = '' }) => {
  const [acknowledged, setAcknowledged] = useState({
    accountSeparation: false,
    descriptions: false,
    references: false
  });

  const allAcknowledged = Object.values(acknowledged).every(Boolean);

  const practices = [
    {
      id: 'accountSeparation',
      title: 'Use business accounts only for business',
      description: 'Keep personal transactions in personal accounts for accurate automation',
      icon: <CheckCircle2 className="w-5 h-5" />
    },
    {
      id: 'descriptions',
      title: 'Include descriptions in payments',
      description: 'Add invoice numbers or vendor names when making bank transfers',
      icon: <CheckCircle2 className="w-5 h-5" />
    },
    {
      id: 'references',
      title: 'Share invoice references with customers',
      description: 'Ask customers to include your invoice number in their payment',
      icon: <CheckCircle2 className="w-5 h-5" />
    }
  ];

  return (
    <div className={className}>
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#108981] to-[#5FC6C5] flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900">Maximize Automation</h2>
        <p className="text-slate-600 mt-2">
          Follow these practices to help our AI match 95%+ of your transactions automatically
        </p>
      </div>

      <div className="space-y-3 mb-6">
        {practices.map((practice) => (
          <div
            key={practice.id}
            className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
              acknowledged[practice.id]
                ? 'border-[#108981] bg-[#108981]/5'
                : 'border-slate-200 hover:border-slate-300'
            }`}
            onClick={() => setAcknowledged(prev => ({ ...prev, [practice.id]: !prev[practice.id] }))}
          >
            <div className="flex items-start gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                acknowledged[practice.id] ? 'bg-[#108981] text-white' : 'bg-slate-200 text-slate-400'
              }`}>
                {acknowledged[practice.id] ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <div className="w-3 h-3 rounded-full border-2 border-current" />
                )}
              </div>
              <div>
                <p className="font-medium text-slate-900">{practice.title}</p>
                <p className="text-sm text-slate-600 mt-0.5">{practice.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => onContinue(false)}
        >
          Skip for now
        </Button>
        <Button
          className={`flex-1 ${allAcknowledged ? 'bg-[#108981] hover:bg-[#108981]/90' : ''}`}
          onClick={() => onContinue(true)}
        >
          {allAcknowledged ? "I understand, continue" : "Continue anyway"}
        </Button>
      </div>
    </div>
  );
};

/**
 * Settings page automation tips section
 */
export const AutomationTipsSection = () => {
  // iter1079 - Comprehensive tips grouped by category. Mirrors the current
  // platform stage: bank feed AI, documents, multi-currency, fixed assets
  // & recognition, loans/investments, period close, tax & compliance,
  // reports & alerts. Each tip explains WHAT to do and WHY it matters.
  const tipCategories = [
    {
      key: 'bank-ai',
      title: 'Bank Feed & AI',
      icon: Zap,
      tint: 'from-amber-400 to-orange-500',
      tips: [
        {
          title: 'Put invoice / bill numbers in every payment description',
          description: 'Include the invoice number (e.g. "INV-00123") or vendor name in bank-transfer narrations. Nothing helps AkountFly pair a payment with the right invoice faster than a matching number in the narration.',
          example: { good: 'INV-00456 · ABC Company payment', bad: 'Transfer' }
        },
        {
          title: 'Confirm the first few AI suggestions - do not "quick-approve"',
          description: 'AkountFly learns from what you confirm. If you quick-approve a wrong suggestion, it will happily repeat the mistake for every similar transaction after. Give each new vendor or customer one careful look; after 2-3 confirmations AkountFly will handle the rest on its own.',
          example: null
        },
        {
          title: 'Never mix business and personal transactions in one account',
          description: 'Personal noise in a business bank feed forces every ambiguous transaction into Review Needed and dilutes the learning signal. Keep business accounts business-only.',
          example: null
        },
        {
          title: 'Ask customers to quote the invoice reference when they pay',
          description: 'When you send an invoice, prompt customers to put "INV-XXXX" in their payment narration. This turns their incoming credit into an instant auto-match against the open invoice.',
          example: null
        },
      ],
    },
    {
      key: 'documents',
      title: 'Documents & Categorisation',
      icon: Lightbulb,
      tint: 'from-emerald-400 to-teal-500',
      tips: [
        {
          title: 'Use consistent vendor names across bills',
          description: 'The AI groups spending by exact vendor name. "MTN Nigeria", "MTN NIG" and "MTN" become three different counterparties. Pick one canonical spelling per vendor and stick to it - your P&L will collapse to clean categories instead of a long tail.',
          example: null
        },
        {
          title: 'Attach receipts to bank charges over ₦500 / $2 / £2',
          description: 'Any tax authority audit will start with unattached charges. The Bank Charges deterministic detector will still post the journal, but attaching the receipt on the transaction row gives you a defensible audit trail.',
          example: null
        },
        {
          title: 'Set a recurring bill for every fixed monthly cost',
          description: 'Rent, subscriptions, utility retainers. Recurring bills pre-create the expected document so the bank tx auto-attaches on the day the money leaves.',
          example: null
        },
      ],
    },
    {
      key: 'fx',
      title: 'Multi-Currency',
      icon: Info,
      tint: 'from-sky-400 to-indigo-500',
      tips: [
        {
          title: 'Refresh FX rates before month-end close',
          description: 'The FX Revaluation step of the month-end checklist uses whatever rate was last cached. Hit the Currency & FX card in More Tools and pull the latest rate on the last business day of the month for a clean revaluation gain/loss journal.',
          example: null
        },
        {
          title: 'Never overwrite the entry-date rate on foreign invoices',
          description: 'AkountFly locks the invoice-date rate on issuance so the FX gain/loss on settlement is calculated correctly. If you edit the rate after the fact, your reconciliation will look off - use Journal reversals instead.',
          example: null
        },
      ],
    },
    {
      key: 'assets',
      title: 'Fixed Assets & Recognition',
      icon: CheckCircle2,
      tint: 'from-violet-400 to-fuchsia-500',
      tips: [
        {
          title: 'Set useful life the day you buy an asset',
          description: 'Depreciation runs automatically each month. If you leave the "useful life" blank when adding a laptop, vehicle or piece of equipment, it will not depreciate at all until you fix it - and back-filling depreciation across closed months is painful. Set it once at purchase and forget it.',
          example: null
        },
        {
          title: 'Split large prepaid expenses into monthly recognitions',
          description: 'A ₦12M annual insurance premium should not hit your P&L in one lump. It should sit on the balance sheet at first, then release ₦1M to expenses each month. Use the Prepaid Expenses screen to schedule this the day you pay.',
          example: null
        },
      ],
    },
    {
      key: 'loans',
      title: 'Loans & Investments',
      icon: Lightbulb,
      tint: 'from-rose-400 to-red-500',
      tips: [
        {
          title: 'Enter the interest rate when you record a loan',
          description: 'AkountFly only accrues monthly interest on loans that have an interest rate filled in. If you leave the rate blank, the interest expense silently misses your P&L each month and your finance costs will look artificially low.',
          example: null
        },
        {
          title: 'Reconcile investment interest every quarter',
          description: 'Interest earned on term deposits and fixed-income investments has to be matched to the bank credit. Use the Investment Portfolio dashboard to confirm each interest posting against the actual receipt.',
          example: null
        },
      ],
    },
    {
      key: 'close',
      title: 'Period Close & Year-End',
      icon: CheckCircle2,
      tint: 'from-teal-400 to-cyan-500',
      tips: [
        {
          title: 'Close every month on the same rhythm',
          description: 'Aim to close month N by the 5th of month N+1. Late closes accumulate uncategorised transactions that then take a week to unpick.',
          example: null
        },
        {
          title: 'Fiscal year-end runs itself - do not run a separate journal',
          description: 'When you close your fiscal-year-end month (December by default, or whatever you set in General Settings), the retained-earnings journal posts automatically. The Closure History tab shows every year-end row distinctly.',
          example: null
        },
        {
          title: 'Unlock a closed period only through the audited flow',
          description: 'On the Month-End tab, use the Unlock Period button (requires a written reason). Never modify posted journals directly - it breaks the audit trail and can silently double-count.',
          example: null
        },
      ],
    },
    {
      key: 'tax',
      title: 'Tax & Compliance',
      icon: AlertCircle,
      tint: 'from-amber-400 to-yellow-500',
      tips: [
        {
          title: 'Review Tax Compliance Setup once per country you operate in',
          description: 'Each country has its own tax types, rates and filing frequencies. If you add a new operating country, walk through Tax & Compliance Setup once to switch on the right taxes.',
          example: null
        },
        {
          title: 'Run the Annual Tax Computation engine monthly',
          description: 'The engine builds a YTD tax picture from your P&L, capital allowances and deferred tax. Running it monthly catches surprises early - by year-end you already know your tax position.',
          example: null
        },
        {
          title: 'Match WHT credits to the underlying invoice',
          description: 'When a customer pays you and deducts withholding tax (WHT), AkountFly can only reconcile the payment cleanly if the WHT rate on your invoice matches what the customer deducted. Set the correct rate on the invoice up front and the credit note lands on the right invoice automatically.',
          example: null
        },
      ],
    },
    {
      key: 'reports',
      title: 'Reports & Alerts',
      icon: Sparkles,
      tint: 'from-indigo-400 to-purple-500',
      tips: [
        {
          title: 'Turn on a weekly summary email',
          description: 'Choose weekly or monthly below - the email lands with your cash position, top 5 expenses and any transactions still in Review Needed. It surfaces problems before your accountant does.',
          example: null
        },
        {
          title: 'Schedule the Bank Charges report for your finance lead',
          description: 'Under Scheduled Reports (More Tools → Data & Imports), auto-email the monthly bank-charges summary. Banks quietly extract 3-5% of small businesses\' cash each year and this report catches it.',
          example: null
        },
        {
          title: 'Drill into every red badge in More Tools > Reports',
          description: 'Red badges surface items that need attention (unreconciled bank tx, unposted journals, expired FX rates). Clearing them weekly keeps month-end close painless.',
          example: null
        },
      ],
    },
  ];

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
            <Lightbulb className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">AkountFly Playbook</h3>
            <p className="text-sm text-slate-600">
              Small habits that keep your books clean, your close painless and your reports trustworthy.
            </p>
          </div>
        </div>

        <div className="space-y-6" data-testid="playbook-categories">
          {tipCategories.map((cat) => (
            <div key={cat.key} data-testid={`playbook-cat-${cat.key}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-7 h-7 rounded-md bg-gradient-to-br ${cat.tint} flex items-center justify-center`}>
                  <cat.icon className="w-4 h-4 text-white" />
                </div>
                <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">{cat.title}</h4>
                <Badge variant="outline" className="text-[10px] font-mono">{cat.tips.length}</Badge>
              </div>
              <div className="space-y-2 pl-1">
                {cat.tips.map((tip, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-slate-50 border border-slate-200 rounded-lg"
                    data-testid={`playbook-tip-${cat.key}-${idx}`}
                  >
                    <p className="font-medium text-slate-900 text-sm mb-1">{tip.title}</p>
                    <p className="text-xs text-slate-600 leading-relaxed">{tip.description}</p>
                    {tip.example && (
                      <div className="mt-2 text-[11px] flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          <span className="text-emerald-700 font-mono bg-emerald-50 px-1.5 py-0.5 rounded truncate">
                            {tip.example.good}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <X className="w-3.5 h-3.5 text-red-500 shrink-0" />
                          <span className="text-red-700 font-mono bg-red-50 px-1.5 py-0.5 rounded truncate">
                            {tip.example.bad}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default {
  BankFeedTipBanner,
  BankAccountSeparationBanner,
  UnmatchedTransactionsTip,
  InvoiceReferenceTip,
  PaymentReferenceHelper,
  OnboardingBestPractices,
  AutomationTipsSection
};
